import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Link } from 'components/common';
import { Box } from 'components/common/box';
import { Icon } from 'components/common/icons';
import { PageSideNavFilters } from '.';

import type { IconProp } from 'components/common/icons/Icon';

type LinkSection = {
  title?: string | React.ReactNode;
  links: {
    href: string;
    icon: IconProp;
    isActive?: boolean;
    text: string;
    unreadItems?: number;
    warningItems?: number;
  }[];
};

interface Props {
  disableFilters?: boolean;
  filterCustomAttributes?: {
    key: string;
    values: string[];
  }[];
  filters?: { [groupHeader: string]: { label: string; value: string }[] };
  filtersSeparate?: {
    [groupHeader: string]: { label: string; value: string }[];
  };
  filtersWithRequireAll?: string[];
  linkSections: LinkSection[];
  shallowNavigation?: boolean;
}

const PageSideNav = ({
  disableFilters = false,
  filterCustomAttributes,
  filters,
  filtersSeparate,
  filtersWithRequireAll,
  linkSections,
  shallowNavigation = false
}: Props) => {
  const { messages, theme } = useReduxState();

  const [isExpanded, setIsExpanded] = useState(false);

  let activeLink: LinkSection['links'][0];
  linkSections.forEach((section) => {
    section.links.forEach((link) => {
      if (link.isActive) activeLink = link;
    });
  });

  return (
    <div className="nav">
      <Box padding={0}>
        <div
          className="mobile-controller"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="controller-inner">
            {activeLink?.icon ? (
              <div className="link-icon">
                <Icon fixedWidth={true} icon={activeLink?.icon || 'search'} />
              </div>
            ) : null}
            {activeLink?.text || messages.commonSearch}
          </div>

          <Icon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
        </div>

        <div className="inner">
          {linkSections.map((section, i) => (
            <div className="section" key={i}>
              {section.title ? (
                <div className="title">{section.title}</div>
              ) : null}

              <div className="links">
                {section.links.map((link) => (
                  <Link
                    href={link.href}
                    key={link.href}
                    style={{ textDecoration: 'none' }}
                    {...(shallowNavigation ? { shallow: true } : {})}
                  >
                    <div
                      className={`link ${link.isActive ? 'link-active' : ''}`}
                    >
                      {link.icon ? (
                        <div className="link-icon">
                          <Icon fixedWidth={true} icon={link.icon} />
                        </div>
                      ) : null}
                      {link.text}
                      {link.unreadItems ? (
                        <div className="unread-items">{link.unreadItems}</div>
                      ) : link.warningItems ? (
                        <div className="warning-items">{link.warningItems}</div>
                      ) : null}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {disableFilters ? null : (
            <PageSideNavFilters
              filterCustomAttributes={filterCustomAttributes}
              filters={filters}
              filtersSeparate={filtersSeparate}
              filtersWithRequireAll={filtersWithRequireAll}
              shallowNavigation={shallowNavigation}
            />
          )}
        </div>
      </Box>

      <style jsx>{`
        .nav {
          margin-bottom: 30px;
          width: 100%;
        }
        .mobile-controller {
          align-items: center;
          border-bottom: ${isExpanded ? 1 : 0}px solid ${theme.lightGrey};
          color: ${theme.textColor};
          cursor: pointer;
          display: flex;
          font-size: 1.4rem;
          font-weight: 600;
          justify-content: space-between;
          padding: 10px 15px;
        }
        .controller-inner {
          display: flex;
          gap: 7px;
        }
        .inner {
          display: ${isExpanded ? 'flex' : 'none'};
          flex-direction: column;
          gap: 5px;
          padding: 15px;
        }
        .section {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .section:not(:first-child) {
          margin-top: 30px;
        }
        .title {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 600;
          padding: 0 12px;
        }
        .links {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .link {
          background-color: white;
          border-radius: 8px;
          color: ${theme.textColor};
          display: flex;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 7px;
          padding: 10px 12px;
          position: relative;
          text-decoration: none;
          transition: background-color 0.3s;
        }
        .link:focus,
        .link:hover {
          background-color: ${theme.lightGrey};
        }
        .link-active {
          background-color: ${theme.lightGrey};
          color: ${theme.textColor};
        }
        .link-icon {
          font-size: 1.2rem;
          position: relative;
          top: 2px;
        }
        .unread-items,
        .warning-items {
          border-radius: 10px;
          font-size: 1.2rem;
          font-weight: 700;
          line-height: 1;
          padding: 3px 6px;
          position: absolute;
          right: 10px;
          top: 10px;
        }
        .unread-items {
          background-color: ${theme.dangerColor};
          color: ${theme.dangerTextColor};
        }
        .warning-items {
          background-color: ${theme.warningColor};
          color: ${theme.warningTextColor};
        }
        @media (min-width: ${breakpoints.medium}px) {
          .nav {
            flex-shrink: 0;
            margin-bottom: 0;
            margin-right: 30px;
            width: 260px;
          }
          .mobile-controller {
            display: none;
          }
          .inner {
            display: flex;
          }
        }
      `}</style>
    </div>
  );
};

export default PageSideNav;
